import { inject, Injectable, signal } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import type { OverlayRef } from '@angular/cdk/overlay';
import { Overlay } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import type { GrowlerConfig } from '../growler.config';
import { DEFAULT_GROWLER_CONFIG } from '../growler.config';
import { GrowlerComponent } from '../growler.component';
import { Severity, SEVERITY_ICON_MAP } from '@app/components/core/severity/severity.config';

@Injectable({ providedIn: 'root' })
export class GrowlerService {
  private overlay = inject(Overlay);
  private document = inject(DOCUMENT);
  private overlayRef: OverlayRef | null = null;

  private readonly _growlerConfigs = signal<GrowlerConfig[]>([]);
  private readonly _hovering = signal(false);
  private readonly _hasFocus = signal(false);

  readonly growlerConfigs = this._growlerConfigs.asReadonly();
  readonly hovering = this._hovering.asReadonly();

  // Growlers waiting to be displayed after the current one is dismissed.
  private readonly _queue: GrowlerConfig[] = [];

  private readonly _timers = new Map<string, ReturnType<typeof setTimeout>>();
  private readonly _remainingTimes = new Map<string, number>();
  private readonly _timerStartTimes = new Map<string, number>();
  private _pauseTimestamp = 0;
  private _idCounter = 0;

  private readonly AUTO_DISMISS_TYPES = new Set(['success', 'info', 'warning', 'error']);
  private readonly DEFAULT_TIMEOUT = 4000;
  private readonly CTA_TIMEOUT = 6000;

  show(config: Partial<GrowlerConfig>): GrowlerConfig {
    const mergedConfig = { ...DEFAULT_GROWLER_CONFIG, ...config } as Partial<GrowlerConfig> &
      typeof DEFAULT_GROWLER_CONFIG;

    const severity = mergedConfig.severity ?? Severity.INFO;
    const style = mergedConfig.growlerStyle;
    const positionClass = mergedConfig.position ? ` growler--position-${mergedConfig.position}` : '';
    const showCloseButton = mergedConfig.closeable;

    const ariaLabelParts = [mergedConfig.title, mergedConfig.message, mergedConfig.items.join(', ')].filter(Boolean);

    const id = `growler-${++this._idCounter}`;

    const newGrowler: GrowlerConfig = {
      ...mergedConfig,
      id,
      severity,
      growlerClasses: `growler growler--${severity} growler--${style}${positionClass}`,
      icon: mergedConfig.icon ?? SEVERITY_ICON_MAP[severity],
      ariaLabel: ariaLabelParts.join('. '),
      showCloseButton
    };

    if (this._growlerConfigs().length === 0) {
      this._displayNow(newGrowler);
    } else {
      this._queue.push(newGrowler);
    }

    return newGrowler;
  }

  private showWithSeverity(severity: Severity, growler: Partial<GrowlerConfig> | string): GrowlerConfig {
    return this.show({
      ...(typeof growler === 'string' ? { message: growler } : growler),
      severity
    });
  }

  success(growler: Partial<GrowlerConfig> | string) {
    return this.showWithSeverity(Severity.SUCCESS, growler);
  }
  info(growler: Partial<GrowlerConfig> | string) {
    return this.showWithSeverity(Severity.INFO, growler);
  }
  warning(growler: Partial<GrowlerConfig> | string) {
    return this.showWithSeverity(Severity.WARNING, growler);
  }
  error(growler: Partial<GrowlerConfig> | string) {
    return this.showWithSeverity(Severity.ERROR, growler);
  }

  dismiss(): void {
    this.clearAllTimers();
    this._queue.length = 0;
    this._growlerConfigs.set([]);
    this.disposeOverlay();
    this._hovering.set(false);
    this._hasFocus.set(false);
    this._pauseTimestamp = 0;
  }

  dismissAt(id: string): void {
    this.clearTimerFor(id);
    this._growlerConfigs.update((stack) => stack.filter((c) => c.id !== id));
    if (this._growlerConfigs().length === 0) {
      this._advanceQueue();
    }
  }

  private _displayNow(growler: GrowlerConfig): void {
    // Invariant: _growlerConfigs always holds exactly ONE entry while a growler is visible.
    // Subsequent show() calls are queued in _queue and shown sequentially via _advanceQueue().
    // showOverlay() therefore always reads _growlerConfigs()[0] safely.
    this.disposeOverlay();
    this._growlerConfigs.set([growler]);
    this.showOverlay();
    if (this.shouldAutoDismiss(growler)) {
      this.startAutoDismissFor(growler.id, growler);
    }
  }

  /** Show the next queued growler, or dispose the overlay when the queue is empty. */
  private _advanceQueue(): void {
    const next = this._queue.shift();
    if (next) {
      this._displayNow(next);
    } else {
      this.disposeOverlay();
    }
  }

  private showOverlay(): void {
    const firstConfig = this._growlerConfigs()[0];
    if (firstConfig?.targetSelector) {
      this.createTargetedOverlay(firstConfig.targetSelector);
    }

    // Fallback: use global overlay when no targetSelector (or target element not found)
    this.overlayRef ??= this.overlay.create({
      hasBackdrop: false,
      panelClass: 'lt-growler-overlay-panel',
      positionStrategy: this.overlay.position().global().centerHorizontally().top('0')
    });

    const portal = new ComponentPortal(GrowlerComponent);
    this.overlayRef.attach(portal);
  }

  private disposeOverlay(): void {
    if (this.overlayRef) {
      this.overlayRef.dispose();
      this.overlayRef = null;
    }
  }

  private createTargetedOverlay(targetSelector: string): void {
    const targetElement = this.document.querySelector(targetSelector);
    if (!targetElement) return;

    const targetRect = targetElement.getBoundingClientRect();

    this.overlayRef = this.overlay.create({
      hasBackdrop: false,
      width: targetRect.width,
      panelClass: 'lt-growler-overlay-panel-targeted',
      positionStrategy: this.overlay
        .position()
        .flexibleConnectedTo(targetElement)
        .withPositions([
          {
            originX: 'start',
            originY: 'top',
            overlayX: 'start',
            overlayY: 'top'
          }
        ])
        .withPush(false),
      scrollStrategy: this.overlay.scrollStrategies.reposition()
    });
  }

  setHovering(isHovering: boolean): void {
    this._hovering.set(isHovering);
    this.updateTimerPauseState();
  }

  setHasFocus(hasFocus: boolean): void {
    this._hasFocus.set(hasFocus);
    this.updateTimerPauseState();
  }

  private updateTimerPauseState(): void {
    if (this._growlerConfigs().length === 0) return;

    const shouldPause = this._hovering() || this._hasFocus();
    shouldPause ? this.pauseAllTimers() : this.resumeAllTimers();
  }

  private pauseAllTimers(): void {
    if (this._pauseTimestamp > 0) return;

    const now = Date.now();
    this._pauseTimestamp = now;

    // Update remaining times before clearing timers
    for (const config of this._growlerConfigs()) {
      const startTime = this._timerStartTimes.get(config.id);
      const remaining = this._remainingTimes.get(config.id);
      if (startTime !== undefined && remaining !== undefined) {
        const elapsed = now - startTime;
        this._remainingTimes.set(config.id, Math.max(0, remaining - elapsed));
      }
    }

    for (const timer of this._timers.values()) {
      clearTimeout(timer);
    }
    this._timers.clear();
    this._timerStartTimes.clear();
  }

  private resumeAllTimers(): void {
    if (this._pauseTimestamp <= 0) return;
    this._pauseTimestamp = 0;

    for (const config of this._growlerConfigs()) {
      const remaining = this._remainingTimes.get(config.id);
      if (remaining === undefined) continue;
      if (remaining > 0) {
        this.scheduleAutoDismiss(config.id, remaining);
      } else {
        this.dismissAt(config.id);
      }
    }
  }

  private startAutoDismissFor(id: string, config: GrowlerConfig): void {
    const defaultTimeout = config.submit ? this.CTA_TIMEOUT : this.DEFAULT_TIMEOUT;
    const timeout = config.timeout ?? defaultTimeout;
    this._remainingTimes.set(id, timeout);
    this.scheduleAutoDismiss(id, timeout);
  }

  private scheduleAutoDismiss(id: string, duration: number): void {
    this._timerStartTimes.set(id, Date.now());
    const timer = setTimeout(() => {
      this.dismissAt(id);
      this._remainingTimes.delete(id);
    }, duration);
    this._timers.set(id, timer);
  }

  private clearTimerFor(id: string): void {
    const timer = this._timers.get(id);
    if (timer) {
      clearTimeout(timer);
      this._timers.delete(id);
    }
    this._remainingTimes.delete(id);
    this._timerStartTimes.delete(id);
  }

  private clearAllTimers(): void {
    for (const timer of this._timers.values()) {
      clearTimeout(timer);
    }
    this._timers.clear();
    this._remainingTimes.clear();
    this._timerStartTimes.clear();
  }

  private shouldAutoDismiss(config: GrowlerConfig): boolean {
    return config.autoDismiss !== false && this.AUTO_DISMISS_TYPES.has(config.severity);
  }
}
